/**
 * DAAM Leads — Analytics page (Phase P2, client-only)
 *
 * Reads /api/leads/analytics?range=7d|30d|90d|180d and renders the
 * mobile-first RTL dashboard. Uses `recharts` (already installed) for
 * the timeline chart. No new dependencies were added.
 *
 * The UI intentionally uses the P2-approved Arabic terminology only:
 *   مشاهدات الصفحة, إجمالي النقرات, نقرات واتساب, نقرات الاتصال,
 *   نقرات الطلب, نسبة تحويل تقديرية, زوار اتخذوا إجراءً — تقديري.
 * Forbidden phrases (طلبات مؤكدة, مبيعات مؤكدة, عملاء مؤكدون) are
 * never rendered.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import { Card, CardContent, CardHeader, CardTitle }
  from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  BarChart3, Loader2, MessageCircle, Phone, ShoppingBag,
  Globe2, Eye, MousePointerClick, PercentSquare, Users2,
  AlertCircle, RefreshCcw,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from "recharts";
import { api } from "@/lib/api";
import LeadsPaywallBanner from "@/components/leads/LeadsPaywallBanner";

const RANGES = [
  { key: "7d",   label: "7 أيام" },
  { key: "30d",  label: "30 يومًا" },
  { key: "90d",  label: "90 يومًا" },
  { key: "180d", label: "180 يومًا" },
];

const SRC_LABELS = {
  instagram: "Instagram", tiktok: "TikTok", snapchat: "Snapchat",
  x: "X (Twitter)", direct: "مباشر", other: "أخرى",
};

function humanErr(e) {
  const d = e?.response?.data?.detail;
  return d?.message_ar || d?.message_en || "تعذّر تحميل النتائج";
}


// ---------------------------------------------------------------------------
// Stat card
// ---------------------------------------------------------------------------
function StatCard({ icon: Icon, label, value, hint, testid }) {
  return (
    <div data-testid={testid}
         className="rounded-2xl border border-neutral-200 bg-white p-4">
      <div className="flex items-start gap-3">
        <span className="h-9 w-9 rounded-xl bg-neutral-100 flex items-center justify-center">
          <Icon className="h-4 w-4 text-neutral-700" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-xs text-neutral-500">{label}</div>
          <div className="mt-1 text-2xl font-semibold text-neutral-900 leading-none">
            {value}
          </div>
          {hint && (
            <div className="text-[11px] text-neutral-400 mt-1.5">{hint}</div>
          )}
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function LeadsAnalyticsPage() {
  const [range,  setRange]  = useState("30d");
  const [state,  setState]  = useState({ status: "loading", data: null });
  const [gate,   setGate]   = useState(null);

  const load = useCallback(async () => {
    setState({ status: "loading", data: null });
    setGate(null);
    try {
      const { data } = await api.get(`/leads/analytics?range=${range}`);
      setState({ status: "ok", data });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      if ((status === 402 || status === 403) && detail?.code) {
        // If the range is disallowed on this tier, silently drop back
        // to 7d (always allowed on every plan) so the page renders
        // with data + we still surface the upgrade hint in-line.
        if (detail.code === "UPGRADE_REQUIRED" && range !== "7d") {
          setRange("7d");
          return;
        }
        setGate(detail);
        setState({ status: "gated", data: null });
        return;
      }
      setState({ status: "error", data: null });
      toast.error(humanErr(e));
    }
  }, [range]);
  useEffect(() => { load(); }, [load]);

  // Backend-provided per-plan capabilities. Missing / empty falls back
  // to "show every range" so the UI degrades gracefully if the API is
  // ever downgraded.
  const allowedRanges = useMemo(() => {
    const list = state.data?.entitlements?.allowed_analytics_ranges;
    return Array.isArray(list) && list.length
      ? new Set(list)
      : new Set(RANGES.map((r) => r.key));
  }, [state.data]);
  const currentTier   = state.data?.entitlements?.tier || null;

  const isEmpty = useMemo(() => {
    const t = state.data?.totals;
    return t && t.page_views === 0 && t.action_clicks === 0;
  }, [state]);

  return (
    <AppShell>
      <div dir="rtl" data-testid="leads-analytics-page"
           className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-8 space-y-6">
        {/* Header */}
        <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-neutral-900">
              نتائج DAAM Leads
            </h1>
            <p className="mt-1 text-sm text-neutral-600 leading-6">
              مؤشرات تقديرية للنمو، محسوبة بتوقيت الرياض.
            </p>
          </div>
          <Button variant="outline" onClick={load} disabled={state.status === "loading"}
                  data-testid="leads-analytics-refresh">
            <RefreshCcw className="h-4 w-4 me-1.5" />
            تحديث
          </Button>
        </header>

        {/* Subscription paywall short-circuit */}
        {state.status === "gated" && gate && (
          <LeadsPaywallBanner
            title={gate.message_ar || "فعّل اشتراكك لاستخدام DAAM Leads"}
            message="أنشئ صفحة نشاطك، قِس الزيارات والنقرات، واعرف أي منصة تحقق نتائج أفضل."
            ctaTo={gate.action?.route || "/app/plans"} />
        )}

        {/* Range tabs — hides ranges that the current plan cannot use.
            The set collapses gracefully to `7d` on Creator so the user
            still sees data + a small upgrade hint below. */}
        {state.status !== "gated" && (
          <div className="flex flex-wrap gap-2" data-testid="leads-range-tabs">
            {RANGES.filter((r) => allowedRanges.has(r.key)).map((r) => (
              <button key={r.key}
                      onClick={() => setRange(r.key)}
                      data-testid={`leads-range-${r.key}`}
                      className={`px-3.5 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                        range === r.key
                          ? "bg-neutral-900 text-white border-neutral-900"
                          : "bg-white text-neutral-700 border-neutral-200 hover:bg-neutral-50"}`}>
                {r.label}
              </button>
            ))}
            {/* Upgrade hint if any range is hidden */}
            {RANGES.some((r) => !allowedRanges.has(r.key)) && currentTier && (
              <a href="/app/plans"
                 data-testid="leads-range-upgrade-cta"
                 className="ms-auto px-3.5 py-1.5 rounded-full text-xs font-medium
                            border border-amber-200 bg-amber-50 text-amber-800
                            hover:bg-amber-100 transition-colors">
                فعّل نطاقات أطول — ترقية الباقة
              </a>
            )}
          </div>
        )}

        {/* Content */}
        {state.status === "loading" && (
          <div data-testid="leads-analytics-loading"
               className="flex justify-center py-24">
            <Loader2 className="h-6 w-6 animate-spin text-neutral-400" />
          </div>
        )}

        {state.status === "error" && (
          <Card data-testid="leads-analytics-error">
            <CardContent className="py-10 text-center">
              <AlertCircle className="h-6 w-6 text-red-500 mx-auto mb-2" />
              <p className="text-sm text-neutral-600">
                تعذّر تحميل النتائج. حاول مجددًا بعد قليل.
              </p>
              <Button variant="outline" className="mt-4" onClick={load}>
                إعادة المحاولة
              </Button>
            </CardContent>
          </Card>
        )}

        {state.status === "ok" && isEmpty && (
          <Card data-testid="leads-analytics-empty">
            <CardContent className="py-14 text-center max-w-md mx-auto">
              <BarChart3 className="h-8 w-8 text-neutral-300 mx-auto mb-3" />
              <p className="text-sm text-neutral-700 leading-6">
                لا توجد نتائج بعد.
                <br />
                أنشئ حملة وشارك رابطها لتبدأ النتائج بالظهور.
              </p>
            </CardContent>
          </Card>
        )}

        {state.status === "ok" && !isEmpty && (
          <AnalyticsBody data={state.data} />
        )}
      </div>
    </AppShell>
  );
}


// ---------------------------------------------------------------------------
// Body — pulled out so the loading/empty/error states above stay clean.
// ---------------------------------------------------------------------------
function AnalyticsBody({ data }) {
  const t = data.totals;
  const conv = t.estimated_conversion_rate;
  const convDisplay = (conv === null || conv === undefined) ? "—" : `${conv}%`;

  return (
    <>
      {/* Six stat cards, 2 per row on mobile */}
      <div className="grid grid-cols-2 gap-3">
        <StatCard testid="stat-page-views"
                  icon={Eye} label="مشاهدات الصفحة"
                  value={t.page_views.toLocaleString("ar-EG")} />
        <StatCard testid="stat-action-clicks"
                  icon={MousePointerClick} label="إجمالي النقرات"
                  value={t.action_clicks.toLocaleString("ar-EG")} />
        <StatCard testid="stat-whatsapp"
                  icon={MessageCircle} label="نقرات واتساب"
                  value={t.whatsapp_clicks.toLocaleString("ar-EG")} />
        <StatCard testid="stat-call"
                  icon={Phone} label="نقرات الاتصال"
                  value={t.call_clicks.toLocaleString("ar-EG")} />
        <StatCard testid="stat-order"
                  icon={ShoppingBag} label="نقرات الطلب"
                  value={t.order_clicks.toLocaleString("ar-EG")} />
        <StatCard testid="stat-website"
                  icon={Globe2} label="نقرات الموقع"
                  value={t.website_clicks.toLocaleString("ar-EG")} />
      </div>

      {/* Conversion + estimated visitors */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <StatCard testid="stat-visitors"
                  icon={Users2}
                  label="زوار اتخذوا إجراءً — تقديري"
                  value={t.estimated_daily_visitors.toLocaleString("ar-EG")}
                  hint="مجموع الزوار المميزين يوميًا. النقرة لا تعني إتمام طلب أو عملية بيع." />
        <StatCard testid="stat-conversion"
                  icon={PercentSquare} label="نسبة تحويل تقديرية"
                  value={convDisplay}
                  hint="النقرات التقديرية ÷ الزوار التقديريين" />
      </div>

      {/* Timeline chart */}
      <Card data-testid="leads-analytics-timeline">
        <CardHeader>
          <CardTitle className="text-base">خط زمني</CardTitle>
        </CardHeader>
        <CardContent className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.timeline}
                       margin={{ top: 6, right: 6, bottom: 4, left: 6 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }}
                     tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip contentStyle={{ direction: "rtl", fontSize: 12 }}
                       labelStyle={{ direction: "ltr" }} />
              <Line type="monotone" dataKey="page_views"
                    stroke="#111827" strokeWidth={2} dot={false}
                    name="مشاهدات" />
              <Line type="monotone" dataKey="action_clicks"
                    stroke="#25D366" strokeWidth={2} dot={false}
                    name="نقرات" />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* By source */}
      <Card data-testid="leads-analytics-by-source">
        <CardHeader>
          <CardTitle className="text-base">توزيع النتائج حسب المنصة</CardTitle>
        </CardHeader>
        <CardContent>
          {data.by_source.length === 0 ? (
            <p className="text-sm text-neutral-400 text-center py-4">
              لا توجد نتائج بعد.
            </p>
          ) : (
            <ul className="divide-y divide-neutral-100">
              {data.by_source.map((s) => (
                <li key={s.source}
                    className="py-2.5 flex items-center justify-between gap-3 text-sm">
                  <span className="text-neutral-800">
                    {SRC_LABELS[s.source] || s.source}
                  </span>
                  <span className="flex items-center gap-3 text-neutral-500 text-xs">
                    <span>مشاهدات: <b className="text-neutral-800">{s.page_views}</b></span>
                    <span>نقرات: <b className="text-neutral-800">{s.action_clicks}</b></span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* By campaign */}
      <Card data-testid="leads-analytics-by-campaign">
        <CardHeader>
          <CardTitle className="text-base">أفضل الحملات</CardTitle>
        </CardHeader>
        <CardContent>
          {data.by_campaign.length === 0 ? (
            <p className="text-sm text-neutral-400 text-center py-4">
              لم تُنشأ حملات بعد.
            </p>
          ) : (
            <ul className="divide-y divide-neutral-100">
              {data.by_campaign.map((c, i) => (
                <li key={c.campaign_id || `no-${i}`}
                    data-testid={`leads-analytics-campaign-${c.code || "none"}`}
                    className="py-3 flex flex-col gap-2 text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-neutral-800 font-medium">
                        {c.name || "بدون حملة"}
                      </div>
                      <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-neutral-400 flex-wrap">
                        {c.code && (
                          <Badge variant="outline" className="font-mono text-[10px]">
                            {c.code}
                          </Badge>
                        )}
                        {c.source && (
                          <Badge variant="outline" className="text-[10px]">
                            {SRC_LABELS[c.source] || c.source}
                          </Badge>
                        )}
                        {c.is_active === false && (
                          <Badge className="bg-neutral-200 text-neutral-700 hover:bg-neutral-200 text-[10px]">
                            معطّلة
                          </Badge>
                        )}
                      </div>
                    </div>
                    <span className="flex items-center gap-3 text-neutral-500 text-xs whitespace-nowrap">
                      <span>مشاهدات: <b className="text-neutral-800">{c.page_views ?? 0}</b></span>
                      <span>نقرات: <b className="text-neutral-800">{c.action_clicks ?? 0}</b></span>
                    </span>
                  </div>
                  {/* Per-CTA breakdown — labelled strictly as clicks, not sales. */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1">
                    <CtaStat label="نقرات واتساب" value={c.whatsapp_clicks ?? 0} tone="bg-[#25D366]/10 text-[#128C7E]" />
                    <CtaStat label="نقرات الاتصال" value={c.call_clicks ?? 0} tone="bg-neutral-100 text-neutral-800" />
                    <CtaStat label="نقرات زر الطلب" value={c.order_clicks ?? 0} tone="bg-amber-100 text-amber-900"
                             hint="عدد النقرات على زر الطلب — ليست عملية بيع مؤكدة" />
                    <CtaStat label="نقرات الموقع" value={c.website_clicks ?? 0} tone="bg-neutral-100 text-neutral-800" />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Disclaimer */}
      <p data-testid="leads-analytics-disclaimer"
         className="text-xs text-neutral-500 leading-6 text-center px-2 pt-2">
        النقرات تعبّر عن اهتمام محتمل ولا تعني طلبًا أو عملية بيع مؤكدة.
      </p>
    </>
  );
}

function CtaStat({ label, value, tone, hint }) {
  return (
    <div title={hint || ""}
         className={`rounded-lg px-2.5 py-1.5 ${tone} flex items-center justify-between gap-2`}>
      <span className="text-[11px] whitespace-nowrap">{label}</span>
      <b className="text-sm">{Number(value).toLocaleString("ar-EG")}</b>
    </div>
  );
}
